import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import MagnetIcon from 'mdi-react/MagnetIcon';
import VectorPointIcon from 'mdi-react/VectorPointIcon';
import DotsGridIcon from 'mdi-react/DotsGridIcon';
import type { SnapSettings } from '../utils/snapping';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';

interface Props {
  readonly settings: SnapSettings;
  readonly onChange: (settings: SnapSettings) => void;
  /** The grid's spacing, ready to print — "0.5 m", or plan units on an unmeasured plan. */
  readonly gridLabel: string;
}

/**
 * The two snapping switches, along the bottom of the plan beside the layers.
 *
 * Chips rather than a menu: each one says what it does and whether it is on, in the place
 * the eye already goes to while drawing, and a single click turns it either way.
 */
const SnapControls = ({ settings, onChange, gridLabel }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const toggle = (
    key: keyof SnapSettings,
    labelKey: string,
    hintKey: string,
    Icon: typeof MagnetIcon,
  ): React.JSX.Element => {
    const on = settings[key];

    return (
      <Tooltip title={String(t(hintKey, { spacing: gridLabel }))}>
        <Chip
          size="small"
          icon={<Icon size={16} />}
          label={String(t(labelKey))}
          clickable
          aria-pressed={on}
          onClick={(): void => onChange({ ...settings, [key]: !on })}
          variant={on ? 'filled' : 'outlined'}
          sx={{
            fontWeight: 600,
            color: on ? editorTheme.accentInk : editorTheme.inkDim,
            backgroundColor: on ? editorTheme.accent : 'transparent',
            borderColor: editorTheme.line,
            '& .MuiChip-icon': { color: 'inherit' },
            '&:hover': { backgroundColor: on ? editorTheme.accent : editorTheme.surfaceSunken },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ flexShrink: 0 }}
      role="group"
      aria-label={String(t('floorplan-simple-snap'))}
    >
      <Box sx={{ display: 'flex', color: editorTheme.inkFaint }}>
        <MagnetIcon size={18} />
      </Box>
      <Typography sx={sectionHeadingSx}>{t('floorplan-simple-snap')}</Typography>
      {toggle('toShapes', 'floorplan-simple-snap-shapes', 'floorplan-simple-snap-shapes-hint', VectorPointIcon)}
      {toggle('toGrid', 'floorplan-simple-snap-grid', 'floorplan-simple-snap-grid-hint', DotsGridIcon)}
    </Stack>
  );
};

export default SnapControls;
